// src/components/KyomuRosterImport.jsx
// 教務システムの「受講者一覧」の取り込み【2026-09-18】
//
//   いきなり入れない。2段階。
//     1. ファイルを選ぶ → サーバーが読んで「何がどこへ入るか」を返す（まだ保存しない）
//     2. 授業ごとに行き先を決めて「取り込む」
//
//   決められること:
//     - 履修時間割番号ごとに、新しく作る / 既存の授業に入れる / 取り込まない
//     - 子コース（主開講が別）を親にまとめるか、別々に作るか
//     - 履修取消の学生を入れるか除くか
//   既存授業の候補には「どこから来た授業か」（manaba由来・手作業）を添える。
//   manaba で作った同名の授業と二重にならないようにするため。
import React, { useEffect, useRef, useState } from 'react';

const auth = () => ({ Authorization: 'Bearer ' + localStorage.getItem('token') });

/**
 * @param {function} onDone           取り込んだあとに親へ知らせる
 * @param {File}     incomingFile     画面全体に落とされたファイル（親から渡る）
 * @param {function} onIncomingTaken  受け取ったら親に空にしてもらう
 */
/**
 * @param {boolean} embedded  true なら自前のファイル選択を出さない。
 *   名簿の入口は1つにまとめてあり、そこから渡ってくる。
 */
export default function KyomuRosterImport({
  onDone, incomingFile, onIncomingTaken, onNotKyomu, embedded,
}) {
  const [file, setFile]       = useState(null);
  const [preview, setPreview] = useState(null);
  const [choice, setChoice]   = useState({});   // key → { to, parent, termId }
  const [excludeCancelled, setExcludeCancelled] = useState(true);
  const [busy, setBusy]       = useState(false);
  const [msg, setMsg]         = useState('');
  const [err, setErr]         = useState('');
  const [result, setResult]   = useState(null);
  const inputRef = useRef(null);

  const reset = () => {
    setFile(null); setPreview(null); setChoice({});
    setMsg(''); setErr(''); setResult(null);
    if (inputRef.current) inputRef.current.value = '';
  };

  // ── 1段目: 読むだけ ──
  const doPreview = async (f) => {
    setBusy(true); setErr(''); setMsg(''); setResult(null);
    try {
      const fd = new FormData();
      fd.append('file', f);
      const r = await fetch('/api/csv-import/kyomu/preview', { method: 'POST', headers: auth(), body: fd });
      const d = await r.json();
      // 受講者一覧ではなかった。manaba の名簿なら親がそちらへ回す【2026-09-18】
      if (!d.success && d.code === 'LOOKS_LIKE_MANABA' && onNotKyomu) {
        setFile(null);
        onNotKyomu(f);
        return;
      }
      if (!d.success) throw new Error(d.message || '読み取れませんでした');
      setPreview(d.preview);
      const init = {};
      d.preview.groups.forEach(g => {
        init[g.key] = { to: g.suggested, parent: g.suggestedParent, termId: g.termId || '' };
      });
      setChoice(init);
      setMsg(`${d.preview.groups.length}件の授業を読み取りました。行き先を確認してください。`);
    } catch (e) { setErr(e.message); setPreview(null); }
    finally { setBusy(false); }
  };

  const pick = (f) => { setFile(f); if (f) doPreview(f); };

  // 画面のどこかに落とされたファイルを受け取る。受け取ったら親に知らせて空にしてもらう
  useEffect(() => {
    if (!incomingFile) return;
    pick(incomingFile);
    if (onIncomingTaken) onIncomingTaken();
  }, [incomingFile]); // eslint-disable-line react-hooks/exhaustive-deps
  const set = (key, patch) => setChoice(c => ({ ...c, [key]: { ...c[key], ...patch } }));

  // ── 2段目: 実際に入れる ──
  const doImport = async () => {
    if (!file) return;
    const going = preview.groups.filter(g => (choice[g.key] || {}).to !== 'skip');
    if (going.length === 0) { setErr('取り込む授業がありません'); return; }
    const students = going.reduce((n, g) => n + (excludeCancelled ? g.counts.active : g.counts.total), 0);
    if (!window.confirm(`授業 ${going.length}件・履修者 ${students}人を取り込みます。よろしいですか？`)) return;

    setBusy(true); setErr(''); setMsg('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('classMap', JSON.stringify(choice));
      fd.append('excludeCancelled', excludeCancelled ? '1' : '0');
      const r = await fetch('/api/csv-import/kyomu', { method: 'POST', headers: auth(), body: fd });
      const d = await r.json();
      if (!d.success) throw new Error(d.message || '取り込めませんでした');
      setResult(d.result);
      setMsg(d.message);
      setPreview(null);
      if (onDone) onDone();
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };

  // 名簿の入口は1つにまとめてある。ここは中身が受講者一覧だったときだけ出る。
  if (embedded && !busy && !preview && !result && !err) return null;

  return (
    <div className="card" data-kyomu-import>
      <div className="card-header"><h2>受講者一覧として取り込む（教務システム）</h2></div>

      {!embedded && (
        <>
          <p style={{ fontSize: '0.85rem', color: '#4b5563', marginTop: 0 }}>
            教務システムが出す「受講者一覧」をそのまま読みます（.xls / .xlsx / .csv）。
            <strong>選んだだけでは保存しません。</strong>
          </p>
          <input ref={inputRef} type="file" accept=".xls,.xlsx,.ods,.csv,.tsv"
                 disabled={busy}
                 onChange={e => pick(e.target.files[0] || null)} />
        </>
      )}

      {busy && <p style={{ color: '#6b7280' }}>処理中…</p>}
      {err && <p style={{ color: '#b91c1c' }}>{err}</p>}
      {msg && <p style={{ color: '#166534' }}>{msg}</p>}

      {/* ── 取り込んだあとの結果 ── */}
      {result && <ImportResult result={result} onReset={reset} embedded={embedded} />}

      {/* ── 確認画面 ── */}
      {preview && (
        <>
          <div className="dash-strip" style={{ cursor: 'default', marginTop: '0.9rem' }}>
            <span className="dash-strip-icon">✓</span>
            <span className="dash-strip-body">
              <span className="dash-strip-t">
                {preview.academicYear}年度 ・ 授業 {preview.groups.length}件 ・ {preview.rowCount}行
              </span>
              <span className="dash-strip-m">
                {preview.fileName}（見出しは{preview.headerRow}行目）
                {preview.skipped > 0 && ` ・ 読み飛ばし ${preview.skipped}行`}
              </span>
            </span>
          </div>

          {/* 書式が変わっても読めるよう、見出しの言い換えや中身から列を探す【2026-09-28】
              見出しがそのまま一致しなかった列と、見つからなかった列だけ知らせる */}
          {preview.columns && (() => {
            const LABEL = { externalCode: '履修時間割番号', courseName: '授業名', courseCode: '科目コード',
              parentCode: '主開講時間割番号', parentName: '主開講授業名', dayPeriod: '開講曜講時',
              studentId: '学籍番号', name: '氏名', nameKana: '氏名カナ', academicYear: '年度',
              teacherName: '教員名', teacherCode: '教員コード', room: '教室',
              cancelReason: '取消理由', cancelDate: '取消年月日', affiliation: '所属', gradeYear: '年次' };
            const guessed = Object.entries(preview.columns).filter(([, v]) => v.how !== 'exact');
            if (guessed.length === 0) return null;
            return (
              <p style={{ color: '#1e40af', fontSize: '0.85rem', margin: '0.5rem 0' }}>
                ℹ 見出しが違う列を読み替えました：
                {guessed.map(([k, v]) => `「${v.header}」→${LABEL[k] || k}${v.how === 'values' ? '（中身から判断）' : ''}`).join('、')}。
                違っていたら取り込まずに教えてください。
              </p>
            );
          })()}
          {(preview.warnings || []).length > 0 && (
            <p style={{ color: '#b45309', fontSize: '0.85rem', margin: '0.5rem 0' }}>
              ⚠ {preview.warnings.join('。')}。その項目は無しで取り込みます。
            </p>
          )}

          {preview.unknownPeriods.length > 0 && (
            <p style={{ color: '#b45309', fontSize: '0.85rem' }}>
              ⚠ {preview.unknownPeriods.join('・')}限の時刻が講時マスタにありません。
              このままだと曜日時限が作られません。教務管理の「講時の時刻」で設定してください。
            </p>
          )}

          <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', margin: '0.8rem 0' }}>
            <input type="checkbox" checked={excludeCancelled}
                   onChange={e => setExcludeCancelled(e.target.checked)} />
            履修取消の学生を入れない
          </label>

          <div style={{ display: 'grid', gap: '0.6rem' }}>
            {preview.groups.map(g => (
              <GroupRow key={g.key} g={g} preview={preview}
                        value={choice[g.key] || {}} onChange={patch => set(g.key, patch)} />
            ))}
          </div>

          <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1.2rem', flexWrap: 'wrap' }}>
            <button className="btn-primary" disabled={busy} onClick={doImport}
                    style={{ minHeight: '3rem', minWidth: '12rem' }}>
              {busy ? '取り込み中…' : '取り込む'}
            </button>
            <button className="btn-secondary" disabled={busy} onClick={reset}>やめる</button>
          </div>
        </>
      )}
    </div>
  );
}

// ── 授業1つぶんの行 ──────────────────────────────────────────────
function GroupRow({ g, value, onChange }) {
  const [open, setOpen] = useState(false);
  const skip = value.to === 'skip';

  return (
    <div style={{
      border: '1px solid ' + (skip ? '#e5e7eb' : '#dbe4f0'),
      borderLeft: '4px solid ' + (skip ? '#cbd5e1' : g.isChild ? '#d97706' : '#2563eb'),
      borderRadius: 10, padding: '0.8rem 1rem',
      background: skip ? '#f8fafc' : '#fff',
      opacity: skip ? 0.7 : 1,
    }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.5rem', flexWrap: 'wrap' }}>
        <strong style={{ fontSize: '1rem' }}>{g.courseName}</strong>
        {g.externalCode && (
          <span style={{ fontSize: '0.75rem', color: '#6b7280', fontFamily: 'monospace' }}>
            {g.externalCode}
          </span>
        )}
        {g.isChild && (
          <span style={{ fontSize: '0.72rem', fontWeight: 700, color: '#b45309',
                         background: '#fffbeb', border: '1px solid #fde68a',
                         borderRadius: 5, padding: '0.05rem 0.4rem' }}>
            子コース
          </span>
        )}
      </div>

      <div style={{ fontSize: '0.82rem', color: '#6b7280', marginTop: '0.2rem' }}>
        {g.termName || g.termText || '学期不明'}
        {g.schedules.length > 0 && ' ・ ' + g.schedules.map(s =>
          `${s.dayName}${s.period}限` + (s.known ? `（${s.startTime}〜${s.endTime}）` : '（時刻未設定）')
        ).join('・')}
        {g.room && ' ・ ' + g.room}
        {' ・ 履修 '}<strong>{g.counts.active}</strong>人
        {g.counts.cancelled > 0 && `（取消 ${g.counts.cancelled}人）`}
      </div>

      {/* 行き先 */}
      <div style={{ marginTop: '0.6rem', display: 'flex', alignItems: 'center', gap: '0.4rem', flexWrap: 'wrap' }}>
        <label style={{ fontSize: '0.82rem', color: '#4b5563' }}>行き先</label>
        <select value={value.to || 'new'} onChange={e => onChange({ to: e.target.value })}>
          <option value="new">新しく作る</option>
          {g.candidates.map(c => (
            <option key={c.id} value={String(c.id)}>
              {c.name}（{c.origin}・履修{c.enrolled}人{c.mine ? '' : '／他の先生'}
              {c.matchedBy === 'code' ? '・番号一致' : '・名前一致'}）
            </option>
          ))}
          <option value="skip">取り込まない</option>
        </select>

        {g.isChild && !skip && (
          <label style={{ fontSize: '0.82rem', display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
            <input type="checkbox" checked={!!value.parent}
                   onChange={e => onChange({ parent: e.target.checked })} />
            「{g.parentName}」にまとめる
            {!g.parentInFile && (
              <span style={{ color: '#b45309' }}>（親がこのファイルにありません）</span>
            )}
          </label>
        )}
      </div>

      {/* 候補があるときは注意を出す */}
      {g.candidates.length > 0 && value.to === 'new' && (
        <p style={{ fontSize: '0.8rem', color: '#b45309', marginTop: '0.35rem' }}>
          ⚠ 似た授業が既にあります。新しく作ると二重になります。
        </p>
      )}

      {/* 取消の学生 */}
      {g.cancelled.length > 0 && (
        <details style={{ marginTop: '0.4rem' }} open={open}
                 onToggle={e => setOpen(e.target.open)}>
          <summary style={{ fontSize: '0.8rem', color: '#2563eb', cursor: 'pointer' }}>
            履修取消 {g.cancelled.length}人を見る
          </summary>
          <div className="table-wrap" style={{ marginTop: '0.4rem' }}>
            <table className="data-table">
              <thead><tr><th>学籍番号</th><th>氏名</th><th>理由</th><th>取消日</th></tr></thead>
              <tbody>
                {g.cancelled.map((s, i) => (
                  <tr key={i}>
                    <td>{s.studentId}</td><td>{s.name}</td>
                    <td>{s.reason || '—'}</td><td>{s.date || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </details>
      )}
    </div>
  );
}

// ── 取り込んだあとの結果 ────────────────────────────────────────
function ImportResult({ result, onReset, embedded }) {
  return (
    <div style={{ marginTop: '0.9rem' }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: '0.5rem' }}>
        <Stat label="作った授業"   value={result.classesCreated} />
        <Stat label="既存に入れた" value={result.classesMatched} />
        <Stat label="履修登録"     value={result.enrolled} />
        <Stat label="作った学生"   value={result.usersCreated} />
        <Stat label="曜日時限"     value={result.schedulesCreated} />
        <Stat label="取消で除外"   value={result.cancelledSkipped} />
      </div>

      {(result.warnings || []).length > 0 && (
        <div style={{ marginTop: '0.8rem' }}>
          {result.warnings.map((w, i) => (
            <p key={i} style={{ color: '#b45309', fontSize: '0.85rem', margin: '0.2rem 0' }}>⚠ {w}</p>
          ))}
        </div>
      )}

      {(result.linked || []).length > 0 && (
        <p style={{ fontSize: '0.85rem', color: '#166534', marginTop: '0.5rem' }}>
          {result.linked.length}件を親の授業にまとめました。
        </p>
      )}

      <div className="table-wrap" style={{ marginTop: '0.8rem' }}>
        <table className="data-table">
          <thead><tr><th>授業</th><th>番号</th><th>学期</th><th>決まり方</th><th>履修</th></tr></thead>
          <tbody>
            {result.classes.map((c, i) => (
              <tr key={i}>
                <td><strong>{c.name}</strong></td>
                <td style={{ fontFamily: 'monospace', fontSize: '0.82rem' }}>{c.externalCode || '—'}</td>
                <td>{c.termName || '—'}</td>
                <td>{{ created: '新規作成', matched: '番号で一致', mapped: '指定' }[c.how] || c.how}</td>
                <td>{c.enrolled}人</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p style={{ fontSize: '0.82rem', color: '#6b7280', marginTop: '0.6rem' }}>
        取り消したいときは「名簿取り込み履歴」から、この取り込み1回ぶんを戻せます。
      </p>
      <button className="btn-secondary" onClick={onReset} style={{ marginTop: '0.5rem' }}>
        {embedded ? '閉じる' : '続けて別のファイルを取り込む'}
      </button>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div style={{ background: '#fff', border: '1px solid #e7ecf3', borderRadius: 10, padding: '0.6rem 0.8rem' }}>
      <div style={{ fontSize: '0.76rem', color: '#8a93a3' }}>{label}</div>
      <div style={{ fontSize: '1.3rem', fontWeight: 700, color: '#334155', fontVariantNumeric: 'tabular-nums' }}>
        {value || 0}
      </div>
    </div>
  );
}
